import React, { useState } from 'react';

const STEPS = [
  {
    id: 1,
    icon: '👶',
    title: '子供の顔を登録する',
    desc: 'まず振り分けたい子供の顔写真を登録します。',
    details: [
      '左メニューの「顔登録」をクリック',
      '「＋ 子供を追加」ボタンをクリック',
      '子供の名前を入力',
      '顔写真を3〜5枚アップロード（正面・明るい写真が最適）',
      '「登録」ボタンをクリック',
    ],
    tips: '複数の子供を登録できます。写真は正面を向いた明るい写真を使うと認識精度が上がります。',
  },
  {
    id: 2,
    icon: '🔗',
    title: 'Google Drive を接続する（任意）',
    desc: 'Google Drive の写真を直接振り分けたい場合は連携します。ローカルフォルダのみ使う場合はスキップ可能です。',
    details: [
      '左メニューの「写真振り分け」をクリック',
      '「Google Drive に接続」ボタンをクリック',
      'Google アカウントでログイン・許可',
      '認証完了のタブが開いたら閉じてアプリに戻る',
      '「接続済み」と表示されれば OK',
    ],
    tips: '接続は一度だけ。次回からは自動でつながります。',
  },
  {
    id: 3,
    icon: '🌈',
    title: '写真を振り分ける',
    desc: '登録した顔情報をもとに、写真を子供ごとのフォルダへ自動仕分けします。',
    details: [
      '左メニューの「写真振り分け」をクリック',
      'Google Drive のフォルダを選択（または Electron 版でローカルフォルダを指定）',
      '保存先フォルダパスを入力（例: /sorted）',
      '「振り分け実行」ボタンをクリック',
      '結果一覧で振り分け状況を確認',
    ],
    tips: '1枚の写真に複数の子供が写っていても、それぞれのフォルダにコピーされます。',
  },
  {
    id: 4,
    icon: '📊',
    title: '利用実績を確認する',
    desc: '振り分けた枚数やプランの状況を確認できます。',
    details: [
      '左メニューの「利用実績」をクリック',
      '累計振り分け枚数・今月の枚数を確認',
      'プランに応じた上限があります',
      'アップグレードはプロフィール→サブスクリプションから',
    ],
    tips: 'フリープランは累計100枚まで。スタンダードプランでは毎月1,000枚振り分けできます。',
  },
];

const FAQ = [
  {
    q: '顔認識の精度はどのくらいですか？',
    a: 'AWS Rekognition を使用しており、業界最高水準の精度です。正面・明るい写真を登録することで、精度がさらに向上します。',
  },
  {
    q: '写真データはどこに保存されますか？',
    a: '顔の特徴データのみ AWS に保存されます。写真ファイル自体はお客様の Google Drive またはローカルに保管されます。',
  },
  {
    q: '認識できなかった写真はどうなりますか？',
    a: '振り分け結果の「未マッチ」として表示されます。手動で確認して対応できます。',
  },
  {
    q: '登録できる子供の人数に制限はありますか？',
    a: 'プランに関わらず人数制限はありません。何人でも登録できます。',
  },
  {
    q: 'Google Drive 以外のストレージは使えますか？',
    a: 'デスクトップアプリ（Electron版）ではローカルフォルダを直接指定できます。他のクラウドストレージは現在対応予定中です。',
  },
];

export default function OnboardingPage() {
  const [openFaq, setOpenFaq] = useState(null);
  const [completedSteps, setCompletedSteps] = useState(new Set());

  const toggleStep = (id) => {
    setCompletedSteps(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  return (
    <div className="page">
      <div className="page-header animate-in">
        <h1 className="page-title">はじめての方へ</h1>
        <p className="page-subtitle">えがおフォルダの使い方ガイド</p>
      </div>

      <div className="page-body animate-in-delay-1" style={{ maxWidth: 720 }}>

        {/* 概要カード */}
        <div style={{
          background: 'linear-gradient(135deg, #FF6B9D22 0%, #9B5FE322 100%)',
          border: '1px solid var(--primary)',
          borderRadius: 16,
          padding: '20px 24px',
          marginBottom: 32,
        }}>
          <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--text-primary)' }}>
            🎉 <strong>えがおフォルダ</strong>へようこそ！<br />
            子供の顔を登録するだけで、大量の写真を自動で子供ごとに振り分けます。<br />
            運動会・発表会・旅行など、写真整理の手間をゼロにしましょう。
          </p>
        </div>

        {/* ステップ */}
        <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, color: 'var(--text-primary)' }}>
          📋 セットアップ手順
        </h2>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 40 }}>
          {STEPS.map((step) => {
            const done = completedSteps.has(step.id);
            return (
              <div key={step.id} style={{
                background: 'var(--surface)',
                border: `1px solid ${done ? 'var(--success)' : 'var(--border)'}`,
                borderRadius: 14,
                padding: '18px 20px',
                transition: 'border-color 0.2s',
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                  {/* ステップ番号 */}
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                    background: done ? 'var(--success)' : 'var(--primary)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 16, color: '#fff', fontWeight: 700,
                  }}>
                    {done ? '✓' : step.id}
                  </div>

                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 18 }}>{step.icon}</span>
                      <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>{step.title}</h3>
                    </div>
                    <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--text-muted)' }}>{step.desc}</p>

                    <ol style={{ margin: '0 0 10px', paddingLeft: 20, fontSize: 13, lineHeight: 2 }}>
                      {step.details.map((d, i) => <li key={i}>{d}</li>)}
                    </ol>

                    <div style={{
                      background: '#FFF9C4',
                      borderLeft: '3px solid #F9A825',
                      borderRadius: '0 8px 8px 0',
                      padding: '8px 12px',
                      fontSize: 12,
                      color: '#5D4037',
                      marginBottom: 12,
                    }}>
                      💡 {step.tips}
                    </div>

                    <button
                      onClick={() => toggleStep(step.id)}
                      className={`btn ${done ? 'btn-secondary' : 'btn-primary'}`}
                      style={{ fontSize: 12, padding: '5px 14px' }}
                    >
                      {done ? '✓ 完了済み' : 'このステップを完了'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* 完了メッセージ */}
        {completedSteps.size === STEPS.length && (
          <div style={{
            background: 'var(--success)',
            color: '#fff',
            borderRadius: 14,
            padding: '20px 24px',
            textAlign: 'center',
            marginBottom: 40,
            fontSize: 15,
            fontWeight: 600,
          }}>
            🎉 セットアップ完了！写真振り分けを始めましょう！
          </div>
        )}

        {/* FAQ */}
        <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, color: 'var(--text-primary)' }}>
          ❓ よくある質問
        </h2>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 40 }}>
          {FAQ.map((item, i) => (
            <div key={i} style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 10,
              overflow: 'hidden',
            }}>
              <button
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                style={{
                  width: '100%', textAlign: 'left', padding: '14px 16px',
                  background: 'none', border: 'none', cursor: 'pointer',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  fontSize: 13, fontWeight: 600, color: 'var(--text-primary)',
                }}
              >
                <span>Q. {item.q}</span>
                <span style={{ fontSize: 16, color: 'var(--text-muted)' }}>{openFaq === i ? '▲' : '▼'}</span>
              </button>
              {openFaq === i && (
                <div style={{
                  padding: '0 16px 14px',
                  fontSize: 13, lineHeight: 1.7,
                  color: 'var(--text-muted)',
                  borderTop: '1px solid var(--border)',
                  paddingTop: 10,
                }}>
                  A. {item.a}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* サポート */}
        <div style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 14,
          padding: '18px 20px',
          textAlign: 'center',
        }}>
          <p style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 600 }}>困ったときは</p>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>
            お問い合わせ: <a href="mailto:info@036base.com" style={{ color: 'var(--primary)' }}>info@036base.com</a>
          </p>
        </div>

      </div>
    </div>
  );
}
